// The brand configuration: everything brand-visible, in one versioned document (spec/03 §12,
// F-ORG-04, #67).
//
// ## Where each value comes from
//
// One configuration, three sources, and **each value has exactly one of them**:
//
//   the deployment     `BrandConfigurationSchema` below: identity, visuals, apps, messaging, legal
//                      and defaults. A JSON file named by `BRAND_CONFIG_FILE`, or — with none — the
//                      Clavian default that ships here (`default.ts`). Validated when the server
//                      boots, which refuses to start on a file it cannot accept; read by the store
//                      apps' build (spec/15 §5). A second Operator is a second deployment with a
//                      different file (ADR-0001).
//   the environment    the Domains group. The hostnames differ per environment (`staging.clavian.io`)
//                      and the server already requires them (`BETTER_AUTH_URL`, `CONSOLE_URL`,
//                      `PLAYER_WEB_URL`, `PUBLIC_MEDIA_URL`), so the file does not name them a second
//                      time for staging to contradict.
//   HQ, at run time    the values spec/14 §14 lets HQ edit — support contacts, the legal documents,
//                      the kiosk's default media — plus the design tokens (so a new accent re-themes
//                      the web surfaces on reload) and the limits on organisation branding.
//                      `BrandRuntimeValuesSchema`: each group HQ has set replaces the deployment's
//                      whole, and a group it has not set (or has reset) is the deployment's. Every
//                      change is a new **revision**, audited (`GET/PUT /v1/hq/brand`).
//
// What clients read is the three resolved into one (`PublicBrandSchema`, served by `GET /v1/meta`),
// with a `revision` that changes whenever any of it does. Messaging is not in it: sender identities
// are the server's business, not a client's.
//
// ## Assets are paths, not URLs
//
// A logo, an icon, the splash and the kiosk's default media are object keys under `brand/` in the
// deployment's `public-media` bucket, served as absolute URLs from its CDN host. So a brand never
// points a surface at a third-party host (the same reason fonts come from a list, `fonts.ts`), and
// staging and production resolve the same file to their own CDN. Left out, it is the Clavian asset
// the surface bundles.
//
// **Absent, never null.** Everywhere a value may be missing it is left out rather than sent as
// `null`: a named shape made nullable becomes a nullable component in the generated document, and
// then every other use of it — a request body included — would take a null it must not.
import { z } from "../zod.js";
import { FileIdSchema } from "../ids/schema.js";
import { AddressSchema } from "../primitives/address.js";
import { LegalDocumentSchema, RefundPolicyLinkSchema } from "../primitives/consent.js";
import { PhoneNumberSchema } from "../primitives/phone-number.js";
import { CountryCodeSchema, CurrencyCodeSchema, IsoDateTimeSchema, TimezoneSchema } from "../primitives/scalars.js";
import { HexColourSchema } from "./colour.js";
import { BrandColoursSchema, BrandRadiusSchema, BrandSurfaceSchema, BrandThemeSchema } from "./theme.js";
/** The shape's version. A file names it; a change that is not additive is a new version. */
export const BRAND_CONFIGURATION_VERSION = 1;
// ---------------------------------------------------------------------------------------------
// Scalars
// ---------------------------------------------------------------------------------------------
export const HttpsUrlSchema = z
    .url({ protocol: /^https$/, hostname: z.regexes.domain })
    .max(2048)
    .openapi('HttpsUrl', { description: 'An absolute https URL.', example: 'https://clavian.io/legal/terms' });
const PATH_SEGMENT = '[A-Za-z0-9_-][A-Za-z0-9._-]*';
/** Logos, icons and the splash: vector or raster. The deployment's own files, never an upload. */
export const BrandAssetPathSchema = z
    .string()
    .max(200)
    .regex(new RegExp(`^brand/(?:${PATH_SEGMENT}/)*${PATH_SEGMENT}\\.(?:svg|png|webp|ico)$`), 'Expected a path under brand/ in public-media, ending .svg, .png, .webp or .ico')
    .openapi('BrandAssetPath', { description: 'An object key under `brand/` in public-media.', example: 'brand/logo-dark.svg' });
/** The kiosk's default media: images and muted video, as the attract loop plays (spec/12 §3.1). */
export const BrandMediaPathSchema = z
    .string()
    .max(200)
    .regex(new RegExp(`^brand/(?:${PATH_SEGMENT}/)*${PATH_SEGMENT}\\.(?:png|webp|jpe?g|mp4|webm)$`), 'Expected a path under brand/ in public-media, ending .png, .webp, .jpg, .mp4 or .webm')
    .openapi('BrandMediaPath', { description: 'An image or video under `brand/` in public-media.', example: 'brand/attract/1.webp' });
/** A BCP 47 tag of the shape the product uses: `en`, `hi`, `en-IN`. */
export const BrandLocaleSchema = z
    .string()
    .regex(/^[a-z]{2,3}(?:-[A-Z]{2})?$/, 'Expected a locale such as en-IN')
    .openapi('BrandLocale', { description: 'A BCP 47 language tag: `en`, `hi`, `en-IN`.', example: 'en-IN' });
// ---------------------------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------------------------
export const BrandSupportSchema = z
    .object({
    email: z.email().max(254),
    phone: PhoneNumberSchema.optional(),
})
    .strict()
    .openapi('BrandSupport', { description: 'Where players and staff get help. Shown in every message the platform sends.' });
export const BrandIdentitySchema = z
    .object({
    /** The product name every surface and message speaks in. Organisations never change it. */
    productName: z.string().min(1).max(40),
    shortName: z.string().min(1).max(20),
    /** The Operator's legal entity, as invoices and receipts name it. Absent until it is incorporated. */
    legalEntityName: z.string().min(1).max(160).optional(),
    registeredAddress: AddressSchema.optional(),
    support: BrandSupportSchema,
    website: HttpsUrlSchema,
})
    .strict()
    .openapi('BrandIdentity', { description: 'Who the Operator is: product name, legal entity, support contacts, website.' });
// ---------------------------------------------------------------------------------------------
// Visuals
// ---------------------------------------------------------------------------------------------
export const BrandLogoSchema = z
    .object({ light: BrandAssetPathSchema.optional(), dark: BrandAssetPathSchema.optional() })
    .strict()
    .openapi('BrandLogo', { description: 'The logo for light and for dark grounds. Absent: the Clavian mark.' });
export const BrandKioskScreensaverSchema = z
    .object({ media: z.array(BrandMediaPathSchema).max(20) })
    .strict()
    .openapi('BrandKioskScreensaver', {
    description: 'The kiosk’s default attract media, for a venue that has uploaded none. Empty: the brand’s animated background.',
});
/**
 * The limits the Operator sets on organisation branding (`org.branding`, spec/03 §12). An
 * organisation may brand its venues' kiosk lock screens and receipts only inside them; the product
 * name and the app identity are never an organisation's to change.
 */
export const OrganizationBrandingLimitsSchema = z
    .object({
    /** An organisation may set its own primary and accent colours. */
    colours: z.boolean(),
    /** An organisation may add its own logo, for light and for dark grounds. */
    logo: z.boolean(),
})
    .strict()
    .openapi('OrganizationBrandingLimits', { description: 'What an organisation may brand on its venues’ lock screens and receipts.' });
export const BrandVisualsSchema = z
    .object({
    logo: BrandLogoSchema,
    appIcon: BrandAssetPathSchema.optional(),
    splash: BrandAssetPathSchema.optional(),
    favicon: BrandAssetPathSchema.optional(),
    kioskScreensaver: BrandKioskScreensaverSchema,
    theme: BrandThemeSchema,
    organizationBranding: OrganizationBrandingLimitsSchema,
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Apps
// ---------------------------------------------------------------------------------------------
export const BrandAppSchema = z
    .object({
    iosBundleId: z
        .string()
        .max(155)
        .regex(/^[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/, 'Expected a reverse-DNS bundle id such as io.clavian.player'),
    androidApplicationId: z
        .string()
        .max(150)
        .regex(/^[a-zA-Z][a-zA-Z0-9_]*(?:\.[a-zA-Z][a-zA-Z0-9_]*)+$/, 'Expected an application id such as io.clavian.player'),
    storeName: z.string().min(1).max(30),
    urlScheme: z.string().max(40).regex(/^[a-z][a-z0-9+.-]*$/, 'Expected a lower-case URL scheme such as clavian'),
})
    .strict()
    .openapi('BrandApp', { description: 'One store app: its bundle and application ids, store name and URL scheme.' });
export const BrandAppsSchema = z
    .object({ player: BrandAppSchema, staff: BrandAppSchema })
    .strict()
    .openapi('BrandApps', { description: 'The two store apps under the Operator’s brand (spec/15 §5).' });
// ---------------------------------------------------------------------------------------------
// Messaging (the server's; not served to clients)
// ---------------------------------------------------------------------------------------------
export const BrandEmailSenderSchema = z
    .object({
    /** The display name. Written into a header, so nothing the composer would have to interpret. */
    name: z.string().regex(/^[^"\\<>\r\n]{1,60}$/, 'Expected a display name of 1–60 characters, without quotes, <, > or line breaks'),
    address: z.email().max(254),
})
    .strict()
    .openapi('BrandEmailSender', { description: 'The From of every email the platform sends.' });
export const BrandSmsSenderSchema = z
    .object({
    /** The sender id (in India, the DLT-registered header). */
    senderId: z.string().regex(/^[A-Za-z0-9]{3,11}$/, 'Expected a sender id of 3–11 letters and digits'),
    /** The DLT principal entity id, in India. */
    dltEntityId: z.string().regex(/^\d{1,30}$/, 'Expected the DLT entity id, digits only').optional(),
})
    .strict()
    .openapi('BrandSmsSender', { description: 'The SMS sender id and its DLT registration.' });
export const BrandWhatsAppSchema = z
    .object({ businessNumber: PhoneNumberSchema })
    .strict()
    .openapi('BrandWhatsApp', { description: 'The WhatsApp business number messages come from.' });
export const BrandMessagingSchema = z
    .object({
    email: BrandEmailSenderSchema,
    /** Absent until an SMS gateway is registered (F-CMP-09). */
    sms: BrandSmsSenderSchema.optional(),
    /** Absent until a WhatsApp business account is (F-CMP-09). */
    whatsapp: BrandWhatsAppSchema.optional(),
})
    .strict()
    .openapi('BrandMessaging', { description: 'Who messages come from: the email sender, the SMS sender id, the WhatsApp number.' });
// ---------------------------------------------------------------------------------------------
// Legal
// ---------------------------------------------------------------------------------------------
/**
 * Wording that must be the Operator's own, per language. Absent: the catalogue's (`@clavian/i18n`).
 * Every other string on every surface comes from the catalogue.
 */
export const BrandLegalCopySchema = z
    .object({
    cookieNotice: z.string().min(1).max(600).optional(),
    consentNotice: z.string().min(1).max(1200).optional(),
})
    .strict()
    .openapi('BrandLegalCopy', { description: 'The cookie notice and the consent wording, where the Operator’s must replace the catalogue’s.' });
/**
 * The legal documents in force (`LegalDocuments`, F-CMP-06): what a client shows before it asks for
 * consent, and what the server holds a sign-up's versions to — plus the Operator's own copy.
 *
 * Written out rather than `LegalDocumentsSchema.extend(…)`: an extended component is emitted as an
 * `allOf`, which the generated clients do not take. It is a `LegalDocuments` all the same — the
 * three fields are the same schemas — and the type checker holds it to that (`legalDocumentsOf`).
 */
export const BrandLegalSchema = z
    .object({
    terms: LegalDocumentSchema,
    privacy: LegalDocumentSchema,
    refund: RefundPolicyLinkSchema,
    copy: z.record(BrandLocaleSchema, BrandLegalCopySchema).optional(),
})
    .strict()
    .openapi('BrandLegal', {
    description: 'The terms, privacy policy and refund policy in force, with the versions a sign-up must have shown, and any copy of the Operator’s own.',
});
/** The documents in force, as consent capture compares them (`LegalDocuments`), without the copy. */
export function legalDocumentsOf(legal) {
    return { terms: legal.terms, privacy: legal.privacy, refund: legal.refund };
}
// ---------------------------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------------------------
export const BrandDefaultsSchema = z
    .object({
    currency: CurrencyCodeSchema,
    locale: BrandLocaleSchema,
    timezone: TimezoneSchema,
    country: CountryCodeSchema,
})
    .strict()
    .openapi('BrandDefaults', { description: 'What a new organisation starts with: currency, locale, timezone, country.' });
// ---------------------------------------------------------------------------------------------
// The deployment's configuration
// ---------------------------------------------------------------------------------------------
export const BrandConfigurationSchema = z
    .object({
    version: z.literal(BRAND_CONFIGURATION_VERSION),
    identity: BrandIdentitySchema,
    visuals: BrandVisualsSchema,
    apps: BrandAppsSchema,
    messaging: BrandMessagingSchema,
    /**
     * Absent: no documents are named, and a consent is recorded with the version the client showed
     * without being compared to anything — what every deployment did before F-ORG-04.
     */
    legal: BrandLegalSchema.optional(),
    defaults: BrandDefaultsSchema,
})
    .strict();
// ---------------------------------------------------------------------------------------------
// HQ's values, at run time
// ---------------------------------------------------------------------------------------------
/** The groups HQ edits at run time, in the order its screen shows them. */
export const BRAND_RUNTIME_GROUPS = ['support', 'legal', 'theme', 'kioskScreensaver', 'organizationBranding'];
/** What HQ has set. A group left out is the deployment's. */
export const BrandRuntimeValuesSchema = z
    .object({
    support: BrandSupportSchema.optional(),
    legal: BrandLegalSchema.optional(),
    theme: BrandThemeSchema.optional(),
    kioskScreensaver: BrandKioskScreensaverSchema.optional(),
    organizationBranding: OrganizationBrandingLimitsSchema.optional(),
})
    .strict()
    .openapi('BrandRuntimeValues', { description: 'The brand values HQ has set. A group left out is the deployment’s.' });
/** Nothing set: every group is the deployment's. Revision 0. */
export const NO_BRAND_RUNTIME_VALUES = {};
/** The deployment's value of each group HQ may edit: what handing a group back gives. */
export const BrandDeploymentValuesSchema = z
    .object({
    support: BrandSupportSchema,
    /** Absent: the deployment names no documents. */
    legal: BrandLegalSchema.optional(),
    theme: BrandThemeSchema,
    kioskScreensaver: BrandKioskScreensaverSchema,
    organizationBranding: OrganizationBrandingLimitsSchema,
})
    .strict()
    .openapi('BrandDeploymentValues', { description: 'The deployment’s value of each group HQ may edit.' });
export const BrandRuntimeGroupSchema = z.enum(BRAND_RUNTIME_GROUPS).openapi('BrandRuntimeGroup', {
    description: 'A group of the brand configuration HQ edits at run time.',
});
/**
 * A change HQ makes: each group sent is set to the value sent, each group in `reset` is handed back
 * to the deployment, and a group in neither is unchanged.
 */
export const BrandRuntimeChangeSchema = z
    .object({
    support: BrandSupportSchema.optional(),
    legal: BrandLegalSchema.optional(),
    theme: BrandThemeSchema.optional(),
    kioskScreensaver: BrandKioskScreensaverSchema.optional(),
    organizationBranding: OrganizationBrandingLimitsSchema.optional(),
    /** Groups to hand back to the deployment's value. */
    reset: z.array(BrandRuntimeGroupSchema).max(BRAND_RUNTIME_GROUPS.length).optional(),
})
    .strict()
    .superRefine((change, context) => {
    const reset = change.reset ?? [];
    const set = BRAND_RUNTIME_GROUPS.filter((group) => change[group] !== undefined);
    if (set.length === 0 && reset.length === 0) {
        context.addIssue({ code: 'custom', path: [], message: 'Set a group, or reset one.' });
    }
    for (const group of reset) {
        if (set.includes(group)) {
            context.addIssue({ code: 'custom', path: ['reset'], message: `"${group}" is both set and reset; send one.` });
        }
    }
    if (new Set(reset).size !== reset.length) {
        context.addIssue({ code: 'custom', path: ['reset'], message: 'A group is reset twice.' });
    }
})
    .openapi('BrandRuntimeChange', {
    description: 'Set groups to new values, or reset them to the deployment’s; groups in neither are unchanged.',
});
// ---------------------------------------------------------------------------------------------
// Organisation branding (`org.branding`)
// ---------------------------------------------------------------------------------------------
/**
 * An organisation's logo, as uploads of its own (`organization_logo`, `POST …/files`): one for light
 * grounds — a receipt, a light lock screen — and one for dark ones, as the Operator's logo has
 * (Visuals). Either may be left out; a surface shows the one for its ground, or none.
 */
export const OrganizationLogoSchema = z
    .object({ light: FileIdSchema.optional(), dark: FileIdSchema.optional() })
    .strict()
    .openapi('OrganizationLogo', { description: 'An organisation’s logo files, for light and for dark grounds.' });
/**
 * What an organisation may add for its venues' kiosk lock screens and receipts (spec/03 §12.3): its
 * own colours, with the same roles and the same AA bars as the Operator's, checked against the
 * Operator's surface, and its own logo. No name — the product name stays the Operator's.
 */
export const OrganizationBrandingSchema = z
    .object({ colour: BrandColoursSchema.optional(), logo: OrganizationLogoSchema.optional() })
    .strict()
    .openapi('OrganizationBranding', { description: 'An organisation’s own colours and logo for its venues’ lock screens and receipts.' });
// ---------------------------------------------------------------------------------------------
// What clients read (`GET /v1/meta`)
// ---------------------------------------------------------------------------------------------
export const BrandLogoUrlsSchema = z
    .object({ light: z.url().optional(), dark: z.url().optional() })
    .openapi('BrandLogoUrls', { description: 'Where the logo for light and for dark grounds is served. Absent: the Clavian mark.' });
export const BrandKioskScreensaverUrlsSchema = z
    .object({ media: z.array(z.url()) })
    .openapi('BrandKioskScreensaverUrls', { description: 'Where the kiosk’s default attract media is served.' });
/**
 * Design-token overrides, by token name (`accent`, `accent-ink`, `radius-md`, `font-display`…),
 * grouped by where they apply: every theme, dark only, light only — the shape `applyBrandTokens()` in
 * `@clavian/ui-web` takes. Empty groups: the Clavian default.
 */
export const BrandTokensSchema = z
    .object({
    shared: z.record(z.string(), z.string()),
    dark: z.record(z.string(), z.string()),
    light: z.record(z.string(), z.string()),
})
    .openapi('BrandTokens', { description: 'Design-token overrides by name: in every theme, in dark only, in light only.' });
export const BrandColourValuesSchema = z
    .object({ primary: HexColourSchema.optional(), accent: HexColourSchema.optional() })
    .openapi('BrandColourValues', { description: 'The brand’s colours. Absent: Clavian’s.' });
export const BrandFontValuesSchema = z
    .object({ display: z.string().optional(), body: z.string().optional() })
    .openapi('BrandFontValues', { description: 'The brand’s font ids. Absent: Clavian’s.' });
export const BrandThemeValuesSchema = z
    .object({
    colour: BrandColourValuesSchema,
    surface: BrandSurfaceSchema,
    radius: BrandRadiusSchema,
    font: BrandFontValuesSchema,
})
    .openapi('BrandThemeValues', { description: 'The brand’s design-token choices, with the defaults filled in where it made none.' });
export const PublicBrandVisualsSchema = z
    .object({
    logo: BrandLogoUrlsSchema,
    /** Absent: the Clavian asset the surface bundles. */
    appIcon: z.url().optional(),
    splash: z.url().optional(),
    favicon: z.url().optional(),
    kioskScreensaver: BrandKioskScreensaverUrlsSchema,
    theme: BrandThemeValuesSchema,
    /** Computed from `theme`: what a surface writes into its CSS variables. */
    tokens: BrandTokensSchema,
    organizationBranding: OrganizationBrandingLimitsSchema,
})
    .openapi('PublicBrandVisuals', { description: 'Logos, icons, default media, the theme and its computed tokens.' });
export const BrandDomainsSchema = z
    .object({
    api: z.string().min(1),
    app: z.string().min(1),
    play: z.string().min(1),
    /** Absent when the deployment serves public media from no host of its own. */
    cdn: z.string().min(1).optional(),
    /** The hosts universal links and app links open the store apps on (spec/15 §5). */
    linkHosts: z.array(z.string().min(1)),
})
    .openapi('BrandDomains', { description: 'The deployment’s hosts: the api, the console, player web, the CDN, and the app-link hosts.' });
export const PublicBrandSchema = z
    .object({
    /** The configuration's shape (`BRAND_CONFIGURATION_VERSION`). */
    version: z.int().min(1).max(1000),
    /** Changes whenever anything below does: compare it to know the brand changed. */
    revision: z.string().regex(/^[0-9a-f]{16}$/),
    identity: BrandIdentitySchema,
    visuals: PublicBrandVisualsSchema,
    domains: BrandDomainsSchema,
    apps: BrandAppsSchema,
    /** Absent: no documents are named, and consent versions are recorded without being compared. */
    legal: BrandLegalSchema.optional(),
    defaults: BrandDefaultsSchema,
})
    .openapi('PublicBrand', { description: 'The brand configuration as clients read it (spec/03 §12).' });
export const HqBrandViewSchema = z
    .object({
    /** HQ's revision: 0 until HQ first changes a value. Also the ETag. */
    revision: z.int().min(0).max(2_147_483_647),
    /** When HQ last changed a value. Absent at revision 0. */
    updatedAt: IsoDateTimeSchema.optional(),
    values: BrandRuntimeValuesSchema,
    deployment: BrandDeploymentValuesSchema,
    messaging: BrandMessagingSchema,
    brand: PublicBrandSchema,
})
    .openapi('HqBrandView', {
    description: 'The brand as HQ edits it: the values HQ has set, the deployment’s, and the brand clients get.',
});
//# sourceMappingURL=configuration.js.map